<template>
    <v-container class="pa-0">
        <v-row>
            <v-col cols="12">
                <div class="students-toolbar">
                    <select-option-students @change="option = $event" iconOnlyMobile />
                    <div class="toolbar-sep" />
                    <div class="search-group">
                        <div class="search-wrap">
                            <kimsa-text-field class="search-field" @change="search = $event" :value="search"
                                :placeholder="$t('common.search')" prepend-inner-icon="mdi-magnify" dense />
                        </div>
                        <button class="filter-btn" :class="{ active: !!filterStudents }" @click="showFilterStudents">
                            <v-icon size="18">mdi-filter-outline</v-icon>
                        </button>
                    </div>
                    <div class="stats-pill">
                        <div class="stat-item">
                            <span class="stat-num">{{ activeCount }}</span>
                            <span class="stat-label">{{ $t('common.actives') }}</span>
                        </div>
                        <div class="stats-divider" />
                        <div class="stat-item stat-item--link" @click="toChats">
                            <span class="stat-num stat-num--accent">{{ messagesCount }}</span>
                            <span class="stat-label stat-label--accent">{{ $tc('common.messages', 2) }}</span>
                        </div>
                    </div>
                </div>
                <!-- Lista / Alumnos: tabla plana -->
                <div v-if="!isGroupedView" class="table-card">
                    <kimsa-grid-table :headers="headers" :loading="loading" :items="students" item-key="id"
                        :per-page="10" :page="page" flat sort-by="status" :no-data-text="$t('common.not_found')">
                        <template v-slot:item.img="{ item }">
                            <div v-if="item.avatar" class="student-avatar"
                                :style="{ backgroundImage: `url(${item.avatar})` }" />
                            <div v-else class="student-avatar-initials" :style="item.avatarGradient">{{ item.initials }}</div>
                        </template>
                        <template v-slot:item.name="{ item }">
                            <div class="student-info pointer" @click="toStudentProfile(item.id)">
                                <div class="student-name">
                                    <span v-if="option === 'student'" class="status-dot"
                                        :class="item.status ? 'dot-inactive' : 'dot-active'" />
                                    <truncated-text :text="item.name" />
                                </div>
                                <div v-if="option === 'student' && subLabel(item)" class="student-team">
                                    {{ subLabel(item) }}
                                </div>
                            </div>
                        </template>
                        <template v-slot:item.group="{ item }">
                            <select-group-student-menu class="list-select-menu" :groups="groups" :student-id="item.id"
                                :student-name="item.name" :current-group="item.groupId"
                                :disabled="!!item.team || !!item.groupId" @save="updateGroup($event)" />
                        </template>
                        <template v-slot:item.team="{ item }">
                            <select-team-student-menu class="list-select-menu" :teams="teams" :student-id="item.id"
                                :student-name="item.name" :current-team="item.team"
                                :disabled="!!item.team || !!item.groupId" @save="updateGroup($event)" />
                        </template>
                        <template v-slot:item.status="{ item }">
                            <kimsa-toggle class="status-cell" :value="!item.status" :loading="item.loadingStatus"
                                @change="changeStatusStudent(item)">
                                <span class="status-label" :class="{ inactive: item.status }">
                                    {{ item.status ? $t('common.inactive') : $t('common.active') }}
                                </span>
                            </kimsa-toggle>
                        </template>
                        <template v-slot:item.playlist="{ item }">
                            <button-tooltip top :tooltip="$t('tooltips.config_playlist')">
                                <template v-slot:activator="{ on, attrs }">
                                    <button class="playlist-cfg-btn" v-bind="attrs" v-on="on"
                                        @click="showPlaylistDialog(item)">
                                        <v-icon size="14">{{ playlistLocked ? 'mdi-lock-outline' : 'mdi-cog-outline' }}</v-icon>
                                    </button>
                                </template>
                            </button-tooltip>
                        </template>
                        <template v-slot:item.alertSettings="{ item }">
                            <button-tooltip top :tooltip="$t('tooltips.alert_settings')">
                                <template v-slot:activator="{ on, attrs }">
                                    <button class="playlist-cfg-btn" v-bind="attrs" v-on="on"
                                        @click="$refs.alertSettings.show(item.id)">
                                        <v-icon size="14">mdi-tune</v-icon>
                                    </button>
                                </template>
                            </button-tooltip>
                        </template>
                    </kimsa-grid-table>
                </div>

                <!-- Grupos / Equipos: bloques agrupados -->
                <div v-else class="groups-view">
                    <v-alert v-if="!loading && groupedBlocks.length === 0" text color="secondary" class="ma-0">
                        {{ $t('common.not_found') }}
                    </v-alert>
                    <div v-for="(block, bi) in groupedBlocks" :key="block.key" class="group-block">
                        <div class="group-header" :class="{ open: isBlockOpen(block.key) }"
                            @click="toggleBlock(block.key)">
                            <div class="group-header-left">
                                <span class="group-name" :style="{ '--block-dot': blockColor(bi) }">{{ blockNameLabel(block.name) }}</span>
                                <span class="group-count">{{ $tc('common.students_count', block.students.length, { n: block.students.length }) }}</span>
                            </div>
                            <div class="group-header-actions">
                                <button-tooltip left :tooltip="$t('tooltips.config_playlist')">
                                    <template v-slot:activator="{ on, attrs }">
                                        <button class="playlist-cfg-btn" v-bind="attrs" v-on="on"
                                            @click.stop="showGroupPlaylistDialog(block)">
                                            <v-icon size="14">{{ playlistLocked ? 'mdi-lock-outline' : 'mdi-cog-outline' }}</v-icon>
                                        </button>
                                    </template>
                                </button-tooltip>
                                <v-icon class="group-chevron" :class="{ open: isBlockOpen(block.key) }" size="18">mdi-chevron-down</v-icon>
                            </div>
                        </div>
                        <v-expand-transition>
                            <div v-show="isBlockOpen(block.key)" class="group-body">
                                <v-virtual-scroll :items="block.students" :item-height="58"
                                    :height="blockBodyHeight(block)" :bench="3">
                                    <template v-slot:default="{ item }">
                                        <div class="group-row">
                                            <div v-if="item.avatar" class="student-avatar"
                                                :style="{ backgroundImage: `url(${item.avatar})` }" />
                                            <div v-else class="student-avatar-initials" :style="item.avatarGradient">{{ item.initials }}</div>
                                            <div class="student-info pointer" @click="toStudentProfile(item.id)">
                                                <div class="student-name">
                                                    <span class="status-dot" :class="item.status ? 'dot-inactive' : 'dot-active'" />
                                                    <truncated-text :text="item.name" />
                                                </div>
                                                <div v-if="subLabel(item)" class="student-team">{{ subLabel(item) }}</div>
                                            </div>
                                            <kimsa-toggle class="status-cell" :value="!item.status" :loading="item.loadingStatus"
                                                @change="changeStatusStudent(item)">
                                                <span class="status-label" :class="{ inactive: item.status }">
                                                    {{ item.status ? $t('common.inactive') : $t('common.active') }}
                                                </span>
                                            </kimsa-toggle>
                                            <button-tooltip top :tooltip="$t('tooltips.config_playlist')">
                                                <template v-slot:activator="{ on, attrs }">
                                                    <button class="playlist-cfg-btn" v-bind="attrs" v-on="on"
                                                        @click="showPlaylistDialog(item)">
                                                        <v-icon size="14">{{ playlistLocked ? 'mdi-lock-outline' : 'mdi-cog-outline' }}</v-icon>
                                                    </button>
                                                </template>
                                            </button-tooltip>
                                            <div class="row-actions">
                                                <button-tooltip left :tooltip="$t('tooltips.alert_settings')">
                                                    <template v-slot:activator="{ on, attrs }">
                                                        <button class="icon-btn" v-bind="attrs" v-on="on"
                                                            @click="$refs.alertSettings.show(item.id)">
                                                            <v-icon size="14">mdi-tune</v-icon>
                                                        </button>
                                                    </template>
                                                </button-tooltip>
                                                <button-tooltip left :tooltip="$t(`tooltips.delete_from_${option}`)">
                                                    <template v-slot:activator="{ on, attrs }">
                                                        <button class="icon-btn del" v-bind="attrs" v-on="on"
                                                            @click="showDeleteStudentConfirm(item)">
                                                            <v-icon size="14">mdi-trash-can-outline</v-icon>
                                                        </button>
                                                    </template>
                                                </button-tooltip>
                                            </div>
                                        </div>
                                    </template>
                                </v-virtual-scroll>
                            </div>
                        </v-expand-transition>
                    </div>
                </div>

                <!-- Footer: leyenda + paginacion (solo vista plana) -->
                <div v-if="!isGroupedView" class="table-footer">
                    <span class="results-count">
                        <span class="results-count-num">{{ students.length }}</span>
                        {{ $t('common.results_count_suffix', { total: studentsRaw.length }) }}
                    </span>
                    <kimsa-pagination v-if="pageCount > 1" class="ml-auto" height="auto" @page="page = $event"
                        :pageCount="pageCount" :total-visible="isMobile ? 4 : 7" />
                </div>

                <students-chat-panel v-if="isMobile" />
            </v-col>
        </v-row>

        <invite-student-dialog ref="invite" />
        <student-invitations-dialog ref="invitations" />
        <create-group-students-dialog ref="createGroup" />
        <create-team-students-dialog ref="createTeam" />
        <admin-groups-dialog ref="adminGroups" />
        <filter-students-dialog :teams="teams" :groups="groups" ref="filter" @filter="onFilterStudents" />
        <config-playlist-mode-dialog ref="playlistDialog" @config="onConfigPlaylistMode" />
        <ai-alert-settings-dialog ref="alertSettings" />

        <kimsa-confirm v-if="option === 'group' || option === 'team'" ref="delete"
            :title="$t(`common.delete_${option}_title`, { name: student && student.name || '' })"
            :content="$t(`common.delete_${option}_subtitle`, { name: student && student.name || '' })"
            :loading="loadingDelete" :persistent="false" @confirm="onConfirmDelete" @cancel="student = null"
            :close-on-confirm="false" :alert-mode="true" />

    </v-container>

</template>

<script>

import { UserIndex } from "@/utils/Algolia";
import InviteStudentDialog from "@/components/coach/InviteStudentDialog";
import StudentInvitationsDialog from "@/components/coach/StudentInvitationsDialog";
import CreateGroupStudentsDialog from "@/components/coach/CreateGroupStudentsDialog";
import { mapActions, mapState, mapMutations } from "vuex";
import SelectGroupStudentMenu from "@/components/coach/SelectGroupStudentMenu";
import CreateTeamStudentsDialog from "@/components/coach/CreateTeamStudentsDialog";
import SelectTeamStudentMenu from "@/components/coach/SelectTeamStudentMenu";
import { db, FieldValue } from "@/utils/Firebase";
import { Storage } from "@/utils/Storage";
import KimsaConfirm from "@/components/common/KimsaConfirm";
import { getAllGroupAssignedRoutines, getAllStudentAssignedRoutines, setStudentGroup, setStudentTeam } from "@/utils/Exercises";
import KimsaTextField from "@/components/common/inputs/KimsaTextField";
import KimsaPagination from "@/components/common/KimsaPagination";
import SelectOptionStudents from "@/components/coach/SelectOptionStudents";
import AdminGroupsDialog from "@/components/coach/AdminGroupsDialog";
import { EventBus } from '@/utils/EventBus';
import { debounce, getInitials, getAvatarGradient } from "@/utils/Common";
import ButtonTooltip from '../../components/common/ButtonTooltip.vue';
import FilterStudentsDialog from '../../components/coach/FilterStudentsDialog.vue';
import ConfigPlaylistModeDialog from '../../components/coach/ConfigPlaylistModeDialog.vue';
import KimsaToggle from '@/components/common/inputs/KimsaToggle';
import TruncatedText from '@/components/common/TruncatedText';
import StudentsChatPanel from '@/components/coach/StudentsChatPanel';
import KimsaGridTable from '@/components/common/KimsaGridTable';
import AiAlertSettingsDialog from '@/components/coach/ai/AiAlertSettingsDialog.vue';

export default {
    components: {
        KimsaGridTable,
        AiAlertSettingsDialog,
        AdminGroupsDialog,
        SelectOptionStudents,
        KimsaPagination,
        KimsaTextField,
        KimsaConfirm,
        SelectTeamStudentMenu,
        CreateTeamStudentsDialog,
        SelectGroupStudentMenu,
        CreateGroupStudentsDialog,
        InviteStudentDialog,
        StudentInvitationsDialog,
        ButtonTooltip,
        FilterStudentsDialog,
        ConfigPlaylistModeDialog,
        KimsaToggle,
        TruncatedText,
        StudentsChatPanel,
    },
    data() {
        return {
            students: [],
            studentsRaw: [],
            loading: false,
            option: 'all',
            student: null,
            loadingDelete: false,
            search: '',
            page: 1,
            filterStudents: null,
            openBlocks: {},
            removedStudentIds: [],
        }
    },
    async mounted() {
        const vm = this
        const removed = vm.$route.query.deletedStudentAccount
        if (removed) vm.removedStudentIds = [removed]
        await vm.loadStudents()
        EventBus.$on('students:invite', vm.showInviteDialog)
        EventBus.$on('students:invitations', vm.showInvitationsDialog)
        EventBus.$on('students:add_group', vm.showCreateGroupDialog)
        EventBus.$on('students:add_team', vm.showCreateTeamDialog)
        EventBus.$on('students:admin', vm.showGroupsListDialog)
    },
    beforeDestroy() {
        const vm = this
        EventBus.$off('students:invite', vm.showInviteDialog)
        EventBus.$off('students:invitations', vm.showInvitationsDialog)
        EventBus.$off('students:add_group', vm.showCreateGroupDialog)
        EventBus.$off('students:add_team', vm.showCreateTeamDialog)
        EventBus.$off('students:admin', vm.showGroupsListDialog)
        vm.setToolbarMeta(null)
    },
    computed: {
        ...mapState(['groups', 'teams']),
        playlistLocked(vm = this) {
            return !vm.can('playlistMode')
        },
        headers(vm = this) {
            if (vm.option === 'student') {
                const headers = [
                    { text: '', value: 'img', sortable: false, width: '56px' },
                    { text: vm.$t('common.name'), value: 'name', width: 'minmax(160px, 1fr)' },
                    { text: vm.$t('common.status'), value: 'status', sortable: false, width: '150px' },
                    { text: vm.$t('common.playlist'), value: 'playlist', sortable: false, width: '90px', align: 'center' },
                ]
                headers.push({ text: vm.$t('common.alert'), value: 'alertSettings', sortable: false, width: '90px', align: 'center' })
                return headers
            }
            return [
                { text: '', value: 'img', sortable: false, width: '56px' },
                { text: vm.$t('common.name'), value: 'name', width: 'minmax(160px, 1fr)' },
                { text: vm.$t('common.group'), value: 'group', sortable: false, width: '150px' },
                { text: vm.$t('common.team'), value: 'team', sortable: false, width: '150px' },
            ]
        },
        pageCount(vm = this) {
            return Math.ceil(vm.students.length / 10)
        },
        isGroupedView(vm = this) {
            return vm.option === 'group' || vm.option === 'team'
        },
        groupedBlocks(vm = this) {
            const map = {}
            if (vm.option === 'group') {
                vm.students.forEach(s => {
                    if (!s.groupId) return
                    if (!map[s.groupId]) map[s.groupId] = { key: s.groupId, name: vm.groupLabel(s.groupId) || vm.$t('common.group'), students: [] }
                    map[s.groupId].students.push(s)
                })
            } else if (vm.option === 'team') {
                vm.students.forEach(s => {
                    if (!s.team) return
                    const key = `${s.team.id}_${s.team.subgroupId}`
                    if (!map[key]) map[key] = { key, name: vm.teamLabel(s.team) || vm.$t('common.team'), students: [] }
                    map[key].students.push(s)
                })
            }
            return Object.values(map)
        },
        activeCount(vm = this) {
            return vm.studentsRaw?.filter(h => !h.studentSubscription?.paused)?.length || 0
        },
        messagesCount(vm = this) {
            return Math.max(0, Number(vm.account?.counter?.messages) || 0)
        }
    },
    methods: {
        ...mapActions(['bindUserAccount']),
        ...mapMutations(['setToolbarMeta']),
        async loadStudents() {
            const vm = this
            vm.loading = true
            vm.page = 1
            vm.students = []

            let filter = `studentSubscription.coachId: ${vm.currentUser.uid}`

            if (vm.filterStudents) {
                if (vm.filterStudents.group) filter += ` AND group:${vm.filterStudents.group}`
                if (vm.filterStudents.team) filter += ` AND team.id:${vm.filterStudents.team}`
                if (vm.filterStudents.subgroupId || vm.filterStudents.subgroupId >= 0) filter += ` AND team.subgroupId:${vm.filterStudents.subgroupId}`
            }

            await UserIndex.search(vm.search, {
                filters: filter,
                hitsPerPage: 1000,
                cacheable: false,
                attributesToRetrieve: [
                    'objectID', 'name', 'email', 'avatar',
                    'group', 'team', 'studentSubscription', 'playlist'
                ]
            })
                .then(({ hits: allHits }) => {
                    const hits = vm.removedStudentIds.length
                        ? allHits.filter(hit => !vm.removedStudentIds.includes(hit.objectID))
                        : allHits
                    vm.studentsRaw = hits
                    vm.students = hits.filter(hit => {
                        if (vm.option === 'group') return !!hit.group
                        if (vm.option === 'team') return !!hit.team
                        if (vm.option === 'student') return !hit.group && !hit.team
                        return hit
                    }).map(hit => {
                        let status = Boolean(hit.studentSubscription.paused)
                        if (hit.studentSubscription.paused === undefined) status = false

                        return {
                            id: hit.objectID,
                            name: hit.name,
                            email: hit.email,
                            avatar: hit.avatar || null,
                            initials: getInitials(hit.name),
                            avatarGradient: getAvatarGradient(hit.objectID || hit.name),
                            groupId: hit.group || null,
                            team: hit.team || null,
                            status: status,
                            loadingStatus: false,
                            playlist: hit.playlist || null
                        }
                    })
                    vm.loading = false
                    vm.reconcileActiveStudents(hits)
                    vm.setToolbarMeta({
                        total: hits.length,
                        active: hits.filter(h => !h.studentSubscription?.paused).length
                    })
                })
                .catch(err => {
                    vm.loading = false
                    vm.toast(vm.$t('common.error_generic'), 'error')
                    console.log(err);
                });
        },
        debouncedLoadStudents: debounce(
            function () {
                this.loadStudents()
            }, 400),
        reconcileActiveStudents(hits) {
            const vm = this
            const noFilters = vm.option === 'all'
                && !vm.search
                && !vm.filterStudents?.group
                && !vm.filterStudents?.team
                && !vm.filterStudents?.subgroupId
                && vm.filterStudents?.subgroupId !== 0
            if (!noFilters) return

            const realActive = hits.filter(h => !h.studentSubscription?.paused).length
            const stored = vm.account?.activeStudents || 0
            if (realActive === stored) return

            db.collection('users').doc(vm.currentUser.uid)
                .update({ activeStudents: realActive })
                .then(() => Storage.setAccount({ ...vm.account, activeStudents: realActive }))
                .catch(err => console.warn('reconcileActiveStudents failed', err))
        },
        toStudentProfile(studentId) {
            const vm = this
            vm.$router.push({ name: 'coach.students.profile', params: { uid: studentId } })
        },
        showInviteDialog() {
            const vm = this
            vm.$refs.invite.show()
        },
        showInvitationsDialog() {
            const vm = this
            vm.$refs.invitations.show()
        },
        showCreateGroupDialog() {
            const vm = this
            if (!vm.requirePlan('createGroup')) return
            vm.$refs.createGroup.show()
        },
        showCreateTeamDialog() {
            const vm = this
            if (!vm.requirePlan('createGroup')) return
            vm.$refs.createTeam.show()
        },
        showGroupsListDialog() {
            const vm = this
            vm.$refs.adminGroups.show()
        },
        updateGroup(studentId) {
            const vm = this
            let studentIndex = vm.students.findIndex(student => student.id === studentId)
            if (studentIndex >= 0) vm.students.splice(studentIndex, 1)
        },
        async changeStatusStudent(student) {
            const vm = this;
            student.loadingStatus = true
            try {
                const newStatus = !student.status

                const batch = db.batch()
                batch.update(db.collection('users').doc(student.id), { 'studentSubscription.paused': newStatus })
                batch.update(db.collection('users').doc(vm.currentUser.uid), {
                    activeStudents: FieldValue.increment(newStatus ? -1 : 1)
                })
                await batch.commit()

                student.status = newStatus
            } catch (error) {
                vm.toast(vm.$t('common.error_generic'), 'error')
            } finally {
                student.loadingStatus = false
            }
        },
        showDeleteStudentConfirm(student) {
            const vm = this
            vm.student = student
            vm.$refs.delete.show()
        },
        async onConfirmDelete() {
            const vm = this
            let studentIndex = vm.students.findIndex(st => st.id === vm.student.id)
            if (studentIndex >= 0) {
                await vm.withLoading(async () => {
                    await vm.clearGroupRoutinesIds()

                    if (vm.option === 'group') {
                        await setStudentGroup(vm.student.id, null, vm.student.groupId)
                    } else if (vm.option === 'team') {
                        await setStudentTeam(vm.student.id, null, vm.student.team)
                    }

                    vm.students.splice(studentIndex, 1)
                }, 'loadingDelete')
            }
            vm.$refs.delete?.hide()
        },
        async clearGroupRoutinesIds() {
            const vm = this

            const studentRoutines = await getAllStudentAssignedRoutines(vm.student.id)
            const studentRoutineIds = new Set(studentRoutines.map(r => r.id))
            if (studentRoutineIds.size === 0) return

            let groupRoutines = []
            if (vm.option === 'group' && vm.student.groupId) {
                groupRoutines = await getAllGroupAssignedRoutines(vm.student.groupId, null)
            } else if (vm.option === 'team' && vm.student.team?.id && vm.student.team?.subgroupId !== undefined) {
                groupRoutines = await getAllGroupAssignedRoutines(vm.student.team.subgroupId, vm.student.team.id)
            } else {
                return
            }

            await Promise.all(groupRoutines.map(gr => {
                const toRemove = (gr.assignedRoutinesIds || []).filter(id => studentRoutineIds.has(id))
                if (toRemove.length === 0) return Promise.resolve()
                return db.collection('groupAssignedRoutines').doc(gr.id).update({
                    assignedRoutinesIds: FieldValue.arrayRemove(...toRemove)
                })
            }))
        },
        toChats() {
            const vm = this
            vm.$router.push({ name: 'coach.students.chats' })
        },
        groupLabel(id) {
            return this.groups.find(group => group.id === id)?.name || ''
        },
        teamLabel(team) {
            const vm = this
            if (!team) return ''
            const found = vm.teams.find(t => t.id === team.id)
            if (!found) return ''
            const subgroups = found.groups || []
            const idx = subgroups.findIndex(g => g.id === team.subgroupId)
            const subgroup = subgroups[idx]
            const subgroupName = subgroup ? (subgroup.name || `${vm.$t('common.subgroup')} ${idx + 1}`) : ''
            return subgroupName ? `${found.name} - ${subgroupName}` : found.name
        },
        subLabel(item) {
            const vm = this
            if (item.groupId) return vm.groupLabel(item.groupId)
            if (item.team) return vm.teamLabel(item.team)
            return ''
        },
        isBlockOpen(key) {
            return !!this.openBlocks[key]
        },
        toggleBlock(key) {
            this.$set(this.openBlocks, key, !this.openBlocks[key])
        },
        blockColor(i) {
            const colors = ['var(--v-primary-base)', 'var(--v-accent-base)', 'var(--v-secondary-base)', 'var(--v-error-base)']
            return colors[i % colors.length]
        },
        blockBodyHeight(block) {
            return Math.min(block.students.length, 7) * 58
        },
        showPlaylistDialog(student) {
            const vm = this;
            if (!vm.requirePlan('playlistMode')) return
            vm.$refs.playlistDialog.show(student)
        },
        showGroupPlaylistDialog(block) {
            const vm = this;
            if (!vm.requirePlan('playlistMode')) return
            const label = vm.$tc('common.students_of_block', block.students.length,
                { n: block.students.length, name: vm.blockNameLabel(block.name) })
            vm.$refs.playlistDialog.showForGroup(block.students, label)
        },
        onConfigPlaylistMode(payload, studentIds) {
            const vm = this;
            const ids = Array.isArray(studentIds) ? studentIds : [studentIds]
            ids.forEach(id => {
                const studentIndex = vm.students.findIndex(student => student.id === id)
                if (studentIndex >= 0) vm.students[studentIndex].playlist = payload
            })
        },
        showFilterStudents() {
            const vm = this;
            vm.$refs.filter.show()
        },
        onFilterStudents(filter = null) {
            const vm = this;
            vm.filterStudents = filter
            vm.loadStudents()
        },
    },
    watch: {
        option() {
            this.loadStudents()
        },
        search() {
            this.debouncedLoadStudents()
        },
        groupedBlocks(blocks) {
            const vm = this
            const next = {}
            blocks.forEach(b => { if (vm.openBlocks[b.key]) next[b.key] = true })
            if (blocks.length && Object.keys(next).length === 0) next[blocks[0].key] = true
            vm.openBlocks = next
        }
    }
}
</script>

<style scoped>
.students-toolbar {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 14px;
}

.toolbar-sep {
    width: 1px;
    height: 28px;
    background: var(--v-outline-base);
    flex-shrink: 0;
}

.search-group {
    display: flex;
    align-items: center;
    gap: 6px;
}

.search-wrap {
    flex: 1 1 auto;
    max-width: 240px;
}

.filter-btn {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline2-base);
    cursor: pointer;
    color: var(--v-txtSecondary-base);
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.15s, color 0.15s;
    flex-shrink: 0;
}

.filter-btn:hover {
    background: var(--v-surface2-base);
    color: var(--v-txtPrimary-base);
}

.filter-btn.active {
    background: rgba(51, 153, 153, 0.12);
    border-color: var(--v-accent-base);
    color: var(--v-accent-base);
}

.stats-pill {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 14px;
    flex-shrink: 0;
}

.stat-item {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    line-height: 1;
}

.stat-item--link {
    cursor: pointer;
    transition: opacity 0.15s;
}

.stat-item--link:hover {
    opacity: 0.75;
}

.stat-num {
    font-size: 18px;
    font-weight: 800;
    color: var(--v-secondary-base);
    letter-spacing: -0.02em;
    line-height: 1;
}

.stat-num--accent {
    color: var(--v-primary-base);
}

.theme--light .stat-num--accent {
    color: var(--v-accent-base);
}

.stat-label {
    font-size: 10px;
    font-weight: 500;
    color: var(--v-txtMuted-base);
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin-top: 3px;
}

.stat-label--accent {
    color: var(--v-primary-base);
    opacity: 0.7;
}

.theme--light .stat-label--accent {
    color: var(--v-accent-base);
}

.stats-divider {
    width: 1px;
    height: 28px;
    background: var(--v-outline-base);
}

@media (max-width: 1100px) {
    .students-toolbar {
        flex-wrap: wrap;
    }

    .toolbar-sep {
        display: none;
    }

    .search-wrap {
        max-width: 100%;
    }
}

@media (max-width: 600px) {
    .stats-pill {
        display: none;
    }
}

/* ── Tabla plana (Lista / Alumnos) ── */
.table-card {
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 14px;
    overflow: hidden;
    box-shadow: 0 1px 0 0 var(--v-outline2-base) inset;
}

/* ── Avatar ── */
.student-avatar {
    width: 36px;
    height: 36px;
    border-radius: 9px;
    background-size: cover;
    background-position: center;
    background-color: var(--v-surface2-base);
}

.student-avatar-initials {
    width: 36px;
    height: 36px;
    border-radius: 9px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.02em;
}

/* ── Nombre + sub ── */
.student-info {
    min-width: 0;
}

.student-name {
    display: flex;
    align-items: center;
    font-size: 14px;
    font-weight: 500;
    color: var(--v-txtPrimary-base);
    min-width: 0;
}

.student-team {
    font-size: 11px;
    color: var(--v-txtMuted-base);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-top: 1px;
}

.status-dot {
    display: inline-block;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    margin-right: 6px;
    flex-shrink: 0;
}

.dot-active {
    background: var(--v-success-base);
}

.dot-inactive {
    background: var(--v-txtMuted-base);
}

/* ── Group/team selector (List) ── */
.list-select-menu {
    font-size: 13px;
    color: var(--v-txtSecondary-base);
}

.list-select-menu >>> .v-btn {
    width: 24px !important;
    height: 24px !important;
}

.list-select-menu >>> .v-btn .v-icon {
    font-size: 16px !important;
    color: var(--v-txtMuted-base);
}

/* ── Status toggle ── */
.status-cell {
    display: flex;
    align-items: center;
    gap: 8px;
}

.status-label {
    font-size: 12px;
    color: var(--v-txtSecondary-base);
}

.status-label.inactive {
    color: var(--v-txtMuted-base);
}

/* ── Playlist ── */
.playlist-cfg-btn {
    width: 30px;
    height: 30px;
    border-radius: 7px;
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline2-base);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--v-txtMuted-base);
    transition: all 0.15s;
}

.playlist-cfg-btn:hover {
    background: linear-gradient(135deg, rgba(204, 255, 51, 0.15), rgba(51, 153, 153, 0.15));
    border-color: rgba(204, 255, 51, 0.45);
    color: var(--v-primary-base);
}

.theme--light .playlist-cfg-btn:hover {
    background: rgba(51, 153, 153, 0.12);
    border-color: var(--v-accent-base);
    color: var(--v-accent-base);
}

/* ── Bloques agrupados (Grupos / Equipos) ── */
.groups-view {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.group-block {
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 12px;
    overflow: hidden;
}

.group-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 20px;
    height: 48px;
    cursor: pointer;
    user-select: none;
    transition: background 0.12s;
    border-bottom: 1px solid transparent;
}

.group-header:hover {
    background: var(--v-surface2-base);
}

.group-header.open {
    border-bottom-color: var(--v-outline-base);
}

.group-header-left {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
}

.group-name {
    display: flex;
    align-items: center;
    font-size: 14px;
    font-weight: 600;
    color: var(--v-txtPrimary-base);
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.group-name::before {
    content: '';
    display: inline-block;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    margin-right: 8px;
    flex-shrink: 0;
    background: var(--block-dot, var(--v-secondary-base));
}

.group-count {
    font-size: 12px;
    color: var(--v-txtMuted-base);
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline2-base);
    border-radius: 10px;
    padding: 1px 8px;
    flex-shrink: 0;
}

.group-header-actions {
    display: flex;
    align-items: center;
    gap: 10px;
}

.group-chevron {
    color: var(--v-txtMuted-base) !important;
    transition: transform 0.2s;
}

.group-chevron.open {
    transform: rotate(180deg);
}

.group-row {
    display: grid;
    grid-template-columns: 44px 1fr 130px 80px 86px;
    align-items: center;
    padding: 0 20px;
    height: 58px;
    border-bottom: 1px solid var(--v-outline-base);
    gap: 12px;
}

.group-row:last-child {
    border-bottom: none;
}

.group-row:hover {
    background: var(--v-surface2-base);
}

.row-actions {
    display: flex;
    align-items: center;
    gap: 5px;
    justify-content: flex-end;
    opacity: 0;
    transition: opacity 0.15s;
}

.group-row:hover .row-actions {
    opacity: 1;
}

.icon-btn {
    width: 26px;
    height: 26px;
    border-radius: 6px;
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline2-base);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--v-txtSecondary-base);
    transition: background 0.15s, color 0.15s, border-color 0.15s;
}

.icon-btn.del:hover {
    color: var(--v-error-base);
    border-color: var(--v-error-base);
    background: rgba(232, 92, 92, 0.08);
}

@media (max-width: 960px) {
    .row-actions {
        opacity: 1;
    }
}

/* ── Footer: leyenda + paginacion ── */
.table-footer {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 10px;
}

.results-count {
    font-size: 12px;
    color: var(--v-txtMuted-base);
}

.results-count-num {
    color: var(--v-secondary-base);
    font-weight: 600;
}
</style>
